import React, { useState } from 'react';

// Import custom hooks
import { useValidationMetrics } from './hooks/useValidationMetrics';
import { useBatchValidation } from './hooks/useBatchValidation';
import { useStoreFiltering } from './hooks/useStoreFiltering';

// Import UI components
import DashboardHeader from './components/DashboardHeader';
import MetricsCards from './components/MetricsCards';
import IssueCategories from './components/IssueCategories';
import BatchControls from './components/BatchControls';
import ValidationResults from './components/ValidationResults';

/**
 * Proofreading Dashboard Component - Refactored Modular Version
 * Main orchestrator component that uses extracted hooks and components
 * @param {Object} props - Component props
 * @param {Array} props.stores - Array of store objects
 * @returns {JSX.Element} Complete dashboard interface
 */
export default function ProofreadingDashboard({ stores = [] }) {
    // Use custom hooks for state management
    const {
        batchValidation,
        validationResults,
        runBatchValidation,
        resetBatchValidation,
        clearValidationResults
    } = useBatchValidation(stores);

    const {
        filterStatus,
        filteredStores,
        setFilterStatus
    } = useStoreFiltering(stores, validationResults);

    const metrics = useValidationMetrics(stores, validationResults);

    // Local state for selected stores
    const [selectedStores, setSelectedStores] = useState([]);

    // Callback functions for BatchControls component
    const callbacks = {
        runBatchValidation,
        setSelectedStores,
        filterStatus,
        setFilterStatus
    };

    return (
        <div className="space-y-8">
            {/* Dashboard Header */}
            <DashboardHeader averageScore={metrics.averageScore} />

            {/* Key Metrics */}
            <MetricsCards statusCounts={metrics.statusCounts} />

            {/* Issue Categories */}
            <IssueCategories issueCategories={metrics.issueCategories} />

            {/* Batch Validation Controls */}
            <BatchControls
                batchValidation={batchValidation}
                selectedStores={selectedStores}
                filteredStores={filteredStores}
                validationResults={validationResults}
                callbacks={callbacks}
            />

            {/* Recent Batch Results */}
            <ValidationResults batchResults={batchValidation.results} />
        </div>
    );
}

// Export named export as well for flexibility
export { ProofreadingDashboard };
